import { Component } from "@theme/component";
import { mediaHoverFine, prefersReducedMotion } from "@theme/utilities";

class VideoCard extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #hoverIntentTimer = null;

  /** @type {boolean} whether the current playback was started by an explicit click, not just a hover preview */
  #explicitPlay = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener("click", this.#onCenterClick, { signal });

    // Respect reduced motion: do not autoplay on hover/focus.
    if (prefersReducedMotion()) return;

    // Only bind hover autoplay for pointer-fine devices.
    if (!mediaHoverFine()) return;

    this.addEventListener("mouseenter", this.#onEnter, { signal });
    this.addEventListener("mouseleave", this.#onLeave, { signal });
    this.addEventListener("focusin", this.#onEnter, { signal });
    this.addEventListener("focusout", this.#onFocusOut, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#clearHoverIntent();
  }

  get deferredMedia() {
    return this.querySelector("deferred-media");
  }

  get content() {
    return this.querySelector(".video-card__content");
  }

  #clearHoverIntent() {
    if (this.#hoverIntentTimer) {
      window.clearTimeout(this.#hoverIntentTimer);
      this.#hoverIntentTimer = null;
    }
  }

  #onEnter = () => {
    this.#clearHoverIntent();

    // Small delay avoids accidental scrubs when moving pointer across a grid.
    this.#hoverIntentTimer = window.setTimeout(() => {
      this.#setActive(true);
      void this.#play();
    }, 120);
  };

  #onLeave = () => {
    this.#clearHoverIntent();
    this.#setActive(false);

    // Don't cut off a video the user explicitly chose to play; only auto-pause
    // the muted hover preview.
    if (!this.#explicitPlay) {
      this.#pause();
    }
  };

  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof HTMLElement && this.contains(next)) return;
    this.#onLeave();
  };

  #setActive(isActive) {
    this.classList.toggle("is-hovering", isActive);
    this.content?.classList.toggle("is-hidden", isActive);
  }

  async #play() {
    const dm = this.deferredMedia;
    if (!dm) return;

    // If poster exists and media isn't loaded, load + play.
    if (!dm.hasAttribute("data-media-loaded")) {
      await dm.showDeferredMedia?.();
      return;
    }

    await dm.playMedia?.();
  }

  #pause() {
    const dm = this.deferredMedia;
    if (!dm) return;
    dm.pauseMedia?.();
  }

  #unmute(video) {
    const dm = this.deferredMedia;
    if (!video || !dm) return;
    video.muted = false;
    dm.updateVolumeButton?.(video);
  }

  #onCenterClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Never override an explicit mute/unmute click.
    if (target.closest('[ref="toggleVolumeButton"]')) return;

    const dm = this.deferredMedia;
    if (!dm) return;

    const isPlayPauseButton = !!target.closest('[ref="toggleMediaButton"]');
    const isDedicatedControl = isPlayPauseButton || !!target.closest(".deferred-media__poster-button");

    const video = dm.querySelector("video");
    const wasPlaying = dm.isPlaying;
    const wasMuted = !video || video.muted;

    if (dm.hasAttribute("data-media-loaded") && !isDedicatedControl) {
      if (wasPlaying && wasMuted) {
        // Already playing silently (autoplay with no cover image, or a hover
        // preview): the first click on the video should unmute it, not pause it.
        this.#unmute(video);
        this.#explicitPlay = true;
        return;
      }

      // Otherwise clicking the video toggles play/pause like the small button.
      dm.toggleMedia?.();
    }

    // Remember whether this click was meant to play or pause, so mouseleave
    // knows not to auto-pause a deliberate click-to-play.
    this.#explicitPlay = !wasPlaying;

    // Unmute when a click starts playback. Skipped for the dedicated play/pause
    // button, since its pause is synchronous and would make "was playing" stale here.
    if (!wasPlaying && !isPlayPauseButton) {
      this.#unmute(video);
    }
  };
}

if (!customElements.get("video-card")) {
  customElements.define("video-card", VideoCard);
}


